import React, { useState } from 'react';
import axios from 'axios';
import { Search, Sparkles, Package, Loader2, MessageSquare, Info, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ProductAssistant = () => {
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [enriching, setEnriching] = useState(false);
  const [enrichStats, setEnrichStats] = useState(null);
  const [includeOutOfStock, setIncludeOutOfStock] = useState(false);
  
  // Example questions
  const exampleQuestions = [
    "Do we have something for ants in the house?",
    "What paint can I use for outdoor walls?",
    "Do you have waterproofing for roofs?",
    "What can I use to seal a leaking pipe?",
    "Do we have sandpaper?",
    "What do you have for cleaning drains?"
  ];

  const askQuestion = async (q = question) => {
    if (!q.trim()) {
      toast.error('Please enter a question');
      return;
    }
    
    setLoading(true);
    setResults(null);
    
    try {
      const response = await axios.post(`${API}/products/ask`, {
        question: q,
        include_out_of_stock: includeOutOfStock
      });
      setResults(response.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Failed to search products');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/products/knowledge/stats`);
      setEnrichStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const enrichProducts = async () => {
    setEnriching(true);
    try {
      const response = await axios.post(`${API}/products/enrich-bulk?limit=20&skip_existing=true`);
      toast.success(`Enriched ${response.data.enriched} products`);
      loadStats();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to enrich products');
    } finally {
      setEnriching(false);
    }
  };

  React.useEffect(() => {
    loadStats();
  }, []);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Sparkles className="h-8 w-8 text-purple-600" />
          Product Assistant
        </h1>
        <p className="text-gray-600 mt-2">
          Ask questions about products in natural language. The AI will find relevant products from your inventory.
        </p>
      </div>

      {/* Stats Card */}
      {enrichStats && (
        <Card className="mb-6 bg-gradient-to-r from-purple-50 to-blue-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex gap-8">
                <div>
                  <p className="text-sm text-gray-600">Products with Knowledge</p>
                  <p className="text-2xl font-bold text-purple-700">
                    {enrichStats.with_description} / {enrichStats.total_products}
                  </p>
                  <p className="text-sm text-gray-500">{enrichStats.enrichment_percentage}% enriched</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">In-Stock Products</p>
                  <p className="text-2xl font-bold text-green-700">
                    {enrichStats.in_stock_with_description} / {enrichStats.in_stock}
                  </p>
                  <p className="text-sm text-gray-500">{enrichStats.in_stock_enrichment_percentage}% enriched</p>
                </div>
              </div>
              <Button 
                onClick={enrichProducts} 
                disabled={enriching}
                variant="outline"
                className="border-purple-300 text-purple-700 hover:bg-purple-50"
              >
                {enriching ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Enriching...</>
                ) : (
                  <><RefreshCw className="h-4 w-4 mr-2" /> Enrich 20 Products</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search Box */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-purple-600" />
            Ask a Question
          </CardTitle>
          <CardDescription>
            Ask about products like a customer would. Example: "Do we have something for ants?"
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="What are you looking for?"
                className="text-lg py-6"
                onKeyDown={(e) => e.key === 'Enter' && askQuestion()}
              />
            </div>
            <Button 
              onClick={() => askQuestion()} 
              disabled={loading}
              className="bg-purple-600 hover:bg-purple-700 px-8"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <><Search className="h-5 w-5 mr-2" /> Search</>
              )}
            </Button>
          </div>
          
          {/* Include out of stock toggle */}
          <label className="flex items-center gap-2 mt-3 text-sm text-gray-600">
            <input 
              type="checkbox" 
              checked={includeOutOfStock}
              onChange={(e) => setIncludeOutOfStock(e.target.checked)}
              className="rounded"
            />
            Include out of stock products
          </label>

          {/* Example Questions */}
          <div className="mt-4">
            <p className="text-sm text-gray-500 mb-2">Try asking:</p>
            <div className="flex flex-wrap gap-2">
              {exampleQuestions.map((q, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setQuestion(q);
                    askQuestion(q);
                  }}
                  className="text-sm px-3 py-1.5 bg-gray-100 hover:bg-purple-100 text-gray-700 rounded-full transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {results && (
        <Card>
          <CardHeader>
            <CardTitle className="text-green-700">
              {results.products?.length > 0 ? (
                <>Found {results.total_found} Product{results.total_found !== 1 ? 's' : ''}</>
              ) : (
                'No Products Found'
              )}
            </CardTitle>
            {results.understanding && (
              <CardDescription className="text-base">
                <strong>Understanding:</strong> {results.understanding}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {results.products?.length > 0 ? (
              <div className="space-y-4">
                {results.products.map((product, index) => (
                  <div 
                    key={product.id || index}
                    className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Package className="h-5 w-5 text-gray-400" />
                          <h3 className="font-semibold text-lg">{product.name}</h3>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">PLU: {product.plu_code}</p>
                        {product.description && (
                          <p className="text-sm text-gray-600 mt-2 bg-blue-50 p-2 rounded">
                            <Info className="h-4 w-4 inline mr-1 text-blue-500" />
                            {product.description}
                          </p>
                        )}
                        {product.reason && (
                          <p className="text-sm text-purple-700 mt-2 italic">
                            💡 {product.reason}
                          </p>
                        )}
                      </div>
                      <div className="text-right ml-4">
                        <p className="text-xl font-bold text-green-700">
                          R {product.selling_price?.toFixed(2)}
                        </p>
                        <Badge 
                          variant={product.current_stock > 0 ? "default" : "destructive"}
                          className={product.current_stock > 0 ? "bg-green-100 text-green-800" : ""}
                        >
                          Stock: {product.current_stock}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">
                No matching products found in your inventory.
              </p>
            )}

            {results.additional_advice && (
              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>💡 Tip:</strong> {results.additional_advice}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProductAssistant;
